<template>
  <div class="drawing-canvas modal panel" @click.self="cancel">
    <div class="panel-container is-animated scale-up" :data-theme="backgroundColor">
      <div v-if="uploading">
        <header class="panel-header" />
        <div class="panel-body">
          <div class="panel-interstitial">
            <interstitial-uploader
              :file="file"
              :download="true"
              title="Saving drawing"
              @done="onUploadDone"
              @cancel="onUploadCancel"
            />
          </div>
        </div>
        <footer class="panel-footer" />
      </div>
      <template v-else>
        <header class="panel-header">
          <div class="panel-left-item">
            <button class="text-button narrow-button" @click="cancel">{{ __('Close') }}</button>
          </div>
          <div class="drawing-canvas-undo">
            <button class="icon-button" :disabled="!canUndo" :title="__('Undo')" @click="undo">
              <base-icon name="undo" />
            </button>
            <button class="icon-button" :disabled="!canRedo" :title="__('Redo')" @click="redo">
              <base-icon name="redo" />
            </button>
          </div>
          <div>
            <div v-if="backgroundSelectorStyle === 'radio'" class="radio-button">
              <label
                ><input
                  type="radio"
                  :checked="backgroundColor == 'white'"
                  value="white"
                  @change="changeBackgroundColor"
                /><span>{{ __('White') }}</span></label
              >
              <label
                ><input
                  type="radio"
                  :checked="backgroundColor == 'black'"
                  value="black"
                  @change="changeBackgroundColor"
                /><span>{{ __('Black') }}</span></label
              >
            </div>
            <div v-else class="select-box">
              <select @change="changeBackgroundColor" @focus="hideSaveButton" @blur="showSaveButton">
                <option value="white">{{ __('White') }}</option>
                <option value="black">{{ __('Black') }}</option>
              </select>
            </div>
          </div>
          <div class="panel-right-item">
            <button v-show="saveButton" class="flat-button narrow-button" @click="save">{{ __('Save') }}</button>
          </div>
        </header>
        <div class="panel-body">
          <canvas
            ref="canvas"
            :style="canvasStyle"
            :width="scaledCanvasWidth"
            :height="scaledCanvasHeight"
            @pointerdown="onPointerDown"
            @mousedown="onMouseDown"
            @touchstart.prevent="onTouchStart"
            @pointerup="onPointerUp"
            @mouseup="onMouseUp"
            @touchend.prevent="onTouchEnd"
            @pointermove="onPointerMove"
            @mousemove="onMouseMove"
            @touchmove.prevent="onTouchMove"
            @mouseout="onMouseOut"
            @pointerout="onPointerOut"
          />
        </div>
        <footer class="panel-footer">
          <div class="color-picker">
            <button
              v-for="(value, key) in availableColors"
              v-if="key != backgroundColor"
              :key="key"
              :class="{ active: colorName == key }"
              @click="pickColor(key)"
            >
              <div :style="{ background: value[0] }" />
            </button>
          </div>
          <div class="brush-picker">
            <button
              :class="{ 'icon-button': true }"
              :style="{ color: brush == 'pen' ? colorHex : null }"
              :title="__('Pen')"
              @click="pickBrush('pen')"
            >
              <base-icon name="edit" />
            </button>
            <button
              :class="{ 'icon-button': true, active: brush == 'eraser' }"
              :title="__('Eraser')"
              @click="pickBrush('eraser')"
            >
              <base-icon name="eraser" />
            </button>
          </div>
        </footer>
      </template>
    </div>
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import BaseIcon from 'vuecomponents/base_icon'
import ContentSourceUploaderMixin from 'vuemixins/content_source_uploader_mixin'
import EscapeToCancelMixin from 'vuemixins/escape_to_cancel_mixin'
import { __ } from 'bits/intl'
import { trackEvent } from 'bits/analytics'
import localStorage from 'bits/safe_storage'
import { captureMessage } from 'bits/error_tracker'

const supportsPointerEvents = !!window.PointerEvent

/* How does it work
  STROKE
  ------
  A mousedown + mousemove (optional) + mouseup combination is called a stroke.
  A stroks is an object with a property called pixels.
  Pixels is an array of x,y pairs which are the pixels this stroke passes through.
  An example stroke = { pixels: [[0,0], [0,1], [1,1]] }
  A stroke can optionally have an id.
  An example stroke with id = { id: 1, pixels: [[0,0], [0,1], [1,1]] }

  STROKE SET
  ----------
  With multi-touch devices, it is possible to have simultaneous strokes.
  A stroke set is an array of simultaneous strokes.
  An example stroke set = [{ id: 1, pixels: [[0,0], [0,1], [1,1]] }, { id: 2, pixels: [[10,10], [10,11], [11,11]] }]

  A stroke set starts when the first finger touches the canvas and ends when the last finger lifts.
  So, say you are drawing with one finger, then add a second finger to the canvas, then a third, then
  lift the second, then third, then first, all of this is considered part of the same stroke set.

  Strokes in the same stroke set have common properties (e.g. color and width)

  Undos and redos happen one stroke set at a time.

*/
export default {
  components: { BaseIcon },
  mixins: [ContentSourceUploaderMixin, EscapeToCancelMixin],
  data() {
    return {
      activeStrokes: [],
      availableColors: {
        black: ['black', __('Black')],
        white: ['white', __('White')],
        red: ['#FA3142', __('Red')],
        blue: ['#157FFB', __('Blue')],
        yellow: ['#FDD230', __('Yellow')],
        green: ['#51D727', __('Green')],
        pink: ['#F17CFC', __('Pink')],
        purple: ['#835FF4', __('Purple')],
        cyan: ['#72E1FD', __('Cyan')],
        orange: ['#FF9800', __('Orange')],
        brown: ['#985213', __('Brown')],
      },
      backgroundColor: localStorage.getItem('canvasBackgroundColor') || 'white',
      brush: 'pen',
      canvasWidth: null,
      canvasHeight: null,
      colorName: 'black',
      colors: [],
      lineWidths: [],
      paint: false,
      redoColors: [],
      redoLineWidths: [],
      redoStrokeSets: [],
      saveButton: true,
      scaledCanvasWidth: null,
      scaledCanvasHeight: null,
      strokeSets: [],
      eventHistory: [],
    }
  },
  computed: {
    backgroundSelectorStyle() {
      if (this.canvasWidth > 320) {
        return 'radio'
      }
      return 'select'
    },
    brushColorName() {
      return this.brush === 'pen' ? this.colorName : this.backgroundColor
    },
    brushSize() {
      return this.brush === 'eraser' ? 21 : 3
    },
    canvasStyle() {
      let cursor = 'crosshair'
      if (this.brush === 'eraser') cursor = 'grabbing'
      return {
        width: `${this.canvasWidth}px`,
        height: `${this.canvasHeight}px`,
        cursor,
      }
    },
    canUndo() {
      return this.strokeSets.length > 0
    },
    canRedo() {
      return this.redoStrokeSets.length > 0
    },
    colorHex() {
      return this.availableColors[this.colorName][0]
    },
    hasDrawn() {
      return this.strokeSets.length > 0 || this.redoStrokeSets.length > 0
    },
  },
  mounted() {
    trackEvent('Drawing Canvas', 'Mounted', this.backgroundColor)
    const canvas = this.$refs.canvas
    const pixelRatio = window.devicePixelRatio
    this.canvasWidth = canvas.clientWidth
    this.canvasHeight = canvas.clientHeight
    this.scaledCanvasWidth = Math.floor(this.canvasWidth * pixelRatio)
    this.scaledCanvasHeight = Math.floor(this.canvasHeight * pixelRatio)
    if (this.backgroundColor === 'black') {
      this.colorName = 'white'
    }
    this.$nextTick(() => {
      const context = canvas.getContext('2d')
      context.scale(pixelRatio, pixelRatio)
      context.fillStyle = this.backgroundColor
      context.fillRect(0, 0, canvas.width, canvas.height)
    })
  },
  methods: {
    addPixels(touches) {
      this.redoStrokeSets = []
      this.redoLineWidths = []
      this.redoColors = []

      const currentStrokeSet = this.strokeSets[this.strokeSets.length - 1]
      if (!currentStrokeSet) {
        captureMessage('Stroke Set Issue With Drawing', {
          context: {
            strokeSetLength: this.strokeSets.length,
            activeStrokeLength: this.activeStrokes.length,
            eventHistory: this.eventHistory,
          },
        })
      }
      const boundingClientRect = this.$refs.canvas.getBoundingClientRect()
      for (const touchEvent of touches) {
        const touchEventId = touchEvent.pointerId ? touchEvent.pointerId : touchEvent.identifier
        const existingTouch = touchEventId ? currentStrokeSet.find((x) => x.id === touchEventId) : currentStrokeSet[0]
        const mouseX = touchEvent.pageX - boundingClientRect.left
        const mouseY = touchEvent.pageY - boundingClientRect.top
        existingTouch.pixels.push([mouseX, mouseY])
      }
      this.redraw()
    },
    addStrokesToCurrentSet(ids) {
      const currentStrokeSet = this.strokeSets[this.strokeSets.length - 1]
      this.strokeSets[this.strokeSets.length - 1] = currentStrokeSet.concat(ids.map((id) => this.newStroke(id)))
    },
    cancel() {
      if (!this.hasDrawn) {
        this.close()
      } else if (confirm(__('You have not saved your drawing. Are you sure you want to close the canvas?'))) {
        this.close()
      }
    },
    changeBackgroundColor(e) {
      const newColor = e.currentTarget.value
      const oldColor = this.backgroundColor
      this.backgroundColor = newColor
      localStorage.setItem('canvasBackgroundColor', newColor)
      if (this.colorName === newColor) {
        this.colorName = oldColor
      }
      this.colors = this.colors.map((x) => {
        if (x === newColor) {
          return oldColor
        }
        if (x === oldColor) {
          return newColor
        }

        return x
      })
      this.$nextTick(() => {
        this.redraw()
      })
      trackEvent('Drawing Canvas', 'Changed Bg', newColor)
    },
    close() {
      this.$emit('close')
    },
    // TODO: Unused
    exportCoordinates() {
      const padding = 16
      let minX = Math.min.apply(null, this.pixelsX)
      let maxX = Math.max.apply(null, this.pixelsX)
      let minY = Math.min.apply(null, this.pixelsY)
      let maxY = Math.max.apply(null, this.pixelsY)
      const canvasWidth = this.$refs.canvas.width
      const canvasHeight = this.$refs.canvas.height
      minX = Math.max(minX - padding, 0)
      maxX = Math.min(maxX + padding, canvasWidth)
      minY = Math.max(minY - padding, 0)
      maxY = Math.min(maxY + padding, canvasHeight)
      return {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
      }
    },
    hideSaveButton() {
      this.saveButton = false
    },
    newStroke(id) {
      return { id: id, pixels: [] }
    },
    onMouseDown(e) {
      if (supportsPointerEvents) return
      this.startNewStrokeSet()
      this.addStrokesToCurrentSet(['id'])
      this.paint = true
      this.addPixels([e])
    },
    onMouseMove(e) {
      if (supportsPointerEvents) return
      if (this.paint) {
        this.addPixels([e])
      }
    },
    onMouseUp() {
      if (supportsPointerEvents) return
      this.paint = false
    },
    onPointerDown(e) {
      this.eventHistory.push('pointerdown')
      // PointerEvent API calls the event listener separately for each touch.
      // So, we have two possibilities here.
      // 1. This is the very first pointer event. If so, start a new set.
      // 2. There were other events. Add this touch to the existing stroke set.

      if (this.activeStrokes.length === 0) {
        // case 1.
        this.startNewStrokeSet()
      }
      this.paint = true
      this.addStrokesToCurrentSet([e.pointerId])
      this.activeStrokes.push(e.pointerId)
      this.addPixels([e])
    },
    onPointerMove(e) {
      this.eventHistory.push('pointermove')
      if (this.paint) {
        this.addPixels([e])
      }
    },
    onPointerUp(e) {
      this.eventHistory.push('pointerup')
      const strokeIdToRemove = e.pointerId
      this.activeStrokes = this.activeStrokes.filter((id) => id !== strokeIdToRemove)
      if (this.activeStrokes.length === 0) {
        this.paint = false
      }
    },
    onTouchEnd(e) {
      if (supportsPointerEvents) return
      const inactiveStrokes = Array.from(e.changedTouches).map((event) => event.identifier)
      this.activeStrokes = this.activeStrokes.filter((id) => !inactiveStrokes.includes(id))
      if (this.activeStrokes.length === 0) {
        this.paint = false
      }
    },
    onTouchMove(e) {
      if (supportsPointerEvents) return
      if (this.paint) {
        const touchList = Array.from(e.touches)
        this.addPixels(touchList)
      }
    },
    onTouchStart(e) {
      if (supportsPointerEvents) return
      this.paint = true
      // Get a list of new touch start events. Multi touch devices give us an identifier for each.
      const touchList = Array.from(e.touches)
      const allTouchEventIdentifiers = touchList.map((event) => event.identifier)

      // We have two possibilities
      // 1. There were no fingers on the screen. This is a brand new touch. If so, start a new set.
      // 2. There are fingers on the screen. Add this touch to the existing stroke set.

      if (this.activeStrokes.length === 0) {
        // case 1.
        this.startNewStrokeSet()
      }
      // Add strokes not already active.
      const newTouchEventIdentifiers = allTouchEventIdentifiers.filter((id) => !this.activeStrokes.includes(id))
      this.addStrokesToCurrentSet(newTouchEventIdentifiers)
      this.activeStrokes = allTouchEventIdentifiers

      this.addPixels(touchList)
    },
    onMouseOut() {
      this.onMouseUp()
    },
    onPointerOut(e) {
      this.onPointerUp(e)
    },
    pickBrush(newBrush) {
      this.brush = newBrush
    },
    pickColor(newColorName) {
      this.colorName = newColorName
    },
    redo() {
      this.strokeSets.push(this.redoStrokeSets.shift())
      this.lineWidths.push(this.redoLineWidths.shift())
      this.colors.push(this.redoColors.shift())
      this.redraw()
    },
    redraw() {
      const strokeSetCount = this.strokeSets.length
      const canvas = this.$refs.canvas
      const context = canvas.getContext('2d')

      context.fillStyle = this.backgroundColor
      context.fillRect(0, 0, canvas.width, canvas.height)
      context.lineJoin = 'round'
      context.lineCap = 'round'

      for (let setIndex = 0; setIndex < strokeSetCount; setIndex++) {
        const strokeSet = this.strokeSets[setIndex]
        for (const stroke of strokeSet) {
          const { pixels } = stroke
          const pixelCount = pixels.length

          context.beginPath()

          if (pixelCount === 0) {
            // do nothing
          } else if (pixelCount === 1) {
            const [x, y] = pixels[0]
            context.moveTo(x - 1, y - 1)
            context.lineTo(x, y)
          } else if (pixelCount === 2) {
            const [controlX, controlY] = pixels[0]
            const [toX, toY] = pixels[1]
            context.quadraticCurveTo(controlX, controlY, toX, toY)
          } else {
            const [startX, startY] = pixels[0]
            context.moveTo(startX, startY)
            // if there are 5 points, index 0-4, this loop goes from index 1-2.
            for (let pixelIndex = 1; pixelIndex <= pixelCount - 3; pixelIndex++) {
              const [controlX, controlY] = pixels[pixelIndex]
              const [nextX, nextY] = pixels[pixelIndex + 1]
              const [toX, toY] = [(controlX + nextX) / 2, (controlY + nextY) / 2]
              context.quadraticCurveTo(controlX, controlY, toX, toY)
            }
            // final point
            const [controlX, controlY] = pixels[pixelCount - 2]
            const [toX, toY] = pixels[pixelCount - 1]
            context.quadraticCurveTo(controlX, controlY, toX, toY)
          }

          context.strokeStyle = this.availableColors[this.colors[setIndex]][0]
          context.lineWidth = this.lineWidths[setIndex]
          context.stroke()
        }
      }
    },
    save() {
      const canvas = this.$refs.canvas
      // DO NOT CROP AT THE MOMENT BECAUSE END RESULT MAYBE TOO NARROW
      // const cropCoordinates = this.exportCoordinates()
      // const croppedCanvas = document.createElement("canvas")
      // croppedCanvas.setAttribute("width", cropCoordinates.width)
      // croppedCanvas.setAttribute("height", cropCoordinates.height)
      // const croppedContext = croppedCanvas.getContext("2d")
      // croppedContext.drawImage(canvas, cropCoordinates.x, cropCoordinates.y, cropCoordinates.width, cropCoordinates.height, 0, 0, cropCoordinates.width, cropCoordinates.height)
      if (canvas.msToBlob) {
        const blob = canvas.msToBlob('image/png')
        blob.name = 'drawing.png'
        this.handleFile(blob)
      } else {
        canvas.toBlob((blob) => {
          // This fails in safari, so don't do it.
          // Instead, set a "name" property on blob.
          // const file = new File([blob], "drawing.png")
          blob.name = 'drawing.png'
          this.handleFile(blob)
        }, 'image/png')
      }
    },
    showSaveButton() {
      this.saveButton = true
    },
    startNewStrokeSet() {
      this.strokeSets.push([])
      this.colors.push(this.brushColorName)
      this.lineWidths.push(this.brushSize)
    },
    undo() {
      this.redoStrokeSets.unshift(this.strokeSets.pop())
      this.redoLineWidths.unshift(this.lineWidths.pop())
      this.redoColors.unshift(this.colors.pop())
      this.redraw()
    },
  },
}
</script>

<style lang="scss" scoped>
@import './../../assets/stylesheets/3/modules/all';
.panel-body {
  height: 480px;
}
.drawing-canvas {
  [data-theme='black'] {
    background-color: black;
    @include inverse-scheme;
  }
  .panel-left-item {
    min-width: unset;
  }
  .panel-right-item {
    min-width: unset;
  }
  canvas {
    display: block;
    flex-grow: 1;
    width: 100%;
    -ms-touch-action: none;
    touch-action: none;
  }
  .color-picker {
    button {
      width: 32px;
      height: 32px;
      margin: 2px;
      padding: 2px;
      border-radius: $default-border-radius;
      box-shadow: none;
      border: none;
      border: 2px solid transparent;
      touch-action: manipulation;
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
      outline: 0;
      background: none;

      div {
        width: 24px;
        height: 24px;
      }

      &.active {
        border-color: $highlight-color;
      }
    }
  }
  .brush-picker {
    display: flex;

    .active {
      color: $highlight-color;
    }
  }
  button[disabled] {
    opacity: 0.2;
  }
}
.drawing-canvas-undo {
  display: flex;
  align-items: center;
}

.radio-button {
  margin: 0 16px;
  border-radius: $default-border-radius;
  span {
    height: 24px;
    line-height: 23px;
    background-color: var(--grey1-color);
    color: var(--muted-text-color);
  }
  input[type='radio']:checked + span {
    background-color: var(--grey3-color);
    color: var(--caption-text-color);
  }
}
@include respondToPhone {
  .color-picker {
    max-width: 204px;
    overflow: auto;
    display: flex;
    -webkit-overflow-scrolling: touch;
  }
}
</style>
